import React, { useCallback, useEffect, useState, useImperativeHandle, forwardRef, useRef } from 'react'
import useCodeMirror from './use-codemirror'
import { useLocation, useHistory } from 'react-router-dom'
import './editor.css'
import { CmdMenu } from './cmdMenu'
import { keymap, EditorView } from '@codemirror/view'
// import AIModal from '../components/AIModal'
import { BalloonMenu } from './balloonMenu'
import { cursorLineDown, cursorLineUp, redo, undo } from '@codemirror/commands'
import { openSearchPanel } from "@codemirror/search"
import SpinnerAndToast from '../SpinnerAndToast'
import { AI_ASSISTANT_DIALOG, CHART_GENERATOR_DIALOG, CONFIRM_DIALOG, IMAGE_UPLOAD_DIALOG, LINK_INPUT_DIALOG, POLL_GENERATOR_DIALOG, TABLE_BUILDER_DIALOG } from 'src/constants/actionTypes'
import { useDispatch, useSelector } from 'react-redux'
import { PollGenerator } from './PollGenerator'
import { useIntl } from 'react-intl'
import { ChartGenerator } from './ChartGenerator'
import { LinkInputModal } from '../common/LinkInputModal'
import { getTargetSlide } from 'src/utils/SlidesUtil'
import { TableBuilderModal } from './TableBuilderModal'
import { AI_DRAFTER } from '../../constants/actionTypes'

const CmEditor = forwardRef((props, ref) => {
  const { onChange, initialDoc, onScroll, onKeyDown, scrollScale, onBlur, app } = props
  const [popupMenuState, setPopupMenuState] = useState({ itemTargeted: 0 })
  const [balloonMenuState, setBalloonMenuState] = useState({})
  const [editorState, setEditorState] = useState();
  const dispatch = useDispatch();
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const params = new Proxy(new URLSearchParams(location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });

  const aiDrafter = useSelector(state => state.uiState.ai_drafter) || {};

  const aiState = useSelector(state => state.uiState.aiDialog) || {};
  useEffect(() => {
    const { response, trigger, doing, operation, visible, caller } = aiState;
    if (visible || !editorState || !['slides', 'markdown'].includes(caller) || !response) {
      return;
    }

    // console.log('operation............', operation)

    let content = ''

    if (response.mimeContents?.length && response.mimeContents[0].src) {
      content = `![](${response.mimeContents[0].src})\n\n`
    }

    content += response.content;

    if (['speechscript', 'speakernotes'].includes(doing?.action)) {
      content = 'Notes: ' + content;
    } else if (doing?.action === 'title') {
      if (!content.startsWith('#')) {
        content = '## ' + content;
      }
    }

    content = content + "\n"

    let selection = editorState.selection.ranges[0];

    if (operation === 'insertBelow') {
      content = "\n" + content;
      let focus = selection.to;
      if (['title', 'keypoints'].includes(doing.action)) {
        let targetSlide = getTargetSlide(editorState.doc.toString(), editorState.doc.lineAt(focus).number)

        focus = targetSlide.currentPageToCurrentLine.start;
      }

      if (!!editorState.doc.lineAt(focus).text?.trim()) {
        content = content + '\n';
      }

      editorView.dispatch({
        changes: { from: focus, to: focus, insert: content },
        selection: { anchor: focus, head: focus + content.length }
      })
    } else if (operation === 'replace') {
      let { from, to } = selection;

      console.log('from... to...', from, to)
      if (doing.action === 'keypoints') {
        let targetSlide = getTargetSlide(editorState.doc.toString(), editorState.doc.lineAt(to).number)
        from = targetSlide.currentPageToCurrentLine.start;
        const notesMatch = targetSlide.currentPage?.match(/note[s]?:/i);
        if (notesMatch) {
          to = targetSlide.currentPageToCurrentLine.start + notesMatch.index;
        }
      }
      content = content + '\n';

      editorView.dispatch({
        changes: { from, to, insert: content },
        selection: { anchor: from, head: from + content.length }
      })
    }

    dispatch({
      type: AI_ASSISTANT_DIALOG,
      value: {
        visible: false
      }
    })
  }, [aiState])

  const tableBuilderState = useSelector(state => state.uiState.tableBuilderDialog) || {};
  useEffect(() => {
    const { data } = tableBuilderState;
    if (tableBuilderState.visible || !data) {
      return;
    }

    const { columns, rows } = data;
    let markdownTable = '|';

    // 生成表头
    for (let i = 0; i < columns; i++) {
      markdownTable += '   |';
    }
    markdownTable += '\n|';

    // 生成表头分隔线
    for (let i = 0; i < columns; i++) {
      markdownTable += '---|';
    }
    markdownTable += '\n';

    // 生成空行
    for (let i = 0; i < rows; i++) {
      markdownTable += '|';
      for (let j = 0; j < columns; j++) {
        markdownTable += '   |';
      }
      markdownTable += '\n';
    }

    markdownTable && insert(-1, markdownTable, markdownTable.length - 1);


    dispatch({
      type: TABLE_BUILDER_DIALOG,
      value: {
        visible: false
      }
    })
  }, [tableBuilderState])

  const linkInputState = useSelector(state => state.uiState.linkInputDialog) || {};
  useEffect(() => {
    const { data } = linkInputState;
    if (linkInputState.visible || !editorState || linkInputState.editor !== 'markdown' || !data) {
      return;
    }

    let str;
    if (linkInputState.trigger === 'link') {
      str = `[${data.text}](${data.link})`;
    } else {
      str = `<iframe frameborder="0" style="width: 100%; height: 80vh" src="${data.link}"></iframe>`;
    }

    if (str) {
      const selection = editorState.selection.ranges[0];
      editorView.dispatch({
        changes: { ...selection, insert: str },
      })
    }

    dispatch({
      type: LINK_INPUT_DIALOG,
      value: {
        visible: false
      }
    })
  }, [linkInputState])

  const imageUploadState = useSelector(state => state.uiState.imageUploadDialog) || {};
  useEffect(() => {
    const { image } = imageUploadState;
    if (imageUploadState.visible || imageUploadState.trigger != 'slides_editor' || !image?.link) {
      return;
    }

    insert(-1, `![${image.altText || ''}](${image.link})`, 1);

    dispatch({
      type: IMAGE_UPLOAD_DIALOG,
      value: {
        visible: false
      }
    })
  }, [imageUploadState])

  const chartGeneratorState = useSelector(state => state.uiState.chartGeneratorDialog) || {};
  useEffect(() => {
    const { data } = chartGeneratorState;
    if (chartGeneratorState.visible || !data) {
      return;
    }

    var csv_data;

    if (data.source === 'url') {
      csv_data = data.url;
    } else {
      csv_data = data.csv;
      csv_data = csv_data.split("\n").filter(row => !!row?.trim());
    }

    insert(-1, makeChartSnippet(data.chart, data.source, csv_data), 0);

    dispatch({
      type: CHART_GENERATOR_DIALOG,
      value: {
        visible: false
      }
    })
  }, [chartGeneratorState])

  const pollGeneratorState = useSelector(state => state.uiState.pollGeneratorDialog) || {};
  useEffect(() => {
    const { data } = pollGeneratorState;
    if (pollGeneratorState.visible || !data) {
      return;
    }

    const options = data.options.filter(o => !!o?.trim());
    const id = new Date().getTime().toString();
    const chart_data = [];
    let row = '';
    options.forEach(option => {
      if (/^[\u4e00-\u9fa5]/.test(option)) {
        option = option.replace(',', '，');
      } else {
        option = option.replace(',', '_');
      }
      row += `, ${option}`
    });

    chart_data.push(row);

    row = '';
    options.forEach(option => {
      row += `, 0`
    });
    chart_data.push(row);


    let pollStr = `${data.desc}\n`
    pollStr += `<div class="poll" data-poll="${id}">\n`;

    options.forEach((option, index) => {
      pollStr += `  <button data-value="${index}">${option}</button>\n  <br>\n`;
    })

    pollStr += '</div>\n' +
      `<p style="font-size:24px;color:gray;">${intl.formatMessage({ id: "poll_responses" })}: <span class="voters" data-poll="${id}">0</span>` +
      '\n</p>' +
      '\n\n---\n\n' +
      '## ' + intl.formatMessage({ id: "poll_result" }) + '\n' +
      makeChartSnippet(data.chart, 'csv', chart_data, [{ name: 'data-poll', value: id }]);

    insert(-1, pollStr, 0);

    dispatch({
      type: POLL_GENERATOR_DIALOG,
      value: {
        visible: false
      }
    })
  }, [pollGeneratorState])

  const makeChartSnippet = useCallback((type, data_type, data, attrs) => {
    const attrs_str = attrs?.map(attr => {
      return `${attr.name}="${attr.value}"`;
    });

    let chart_str = '<div style="height:480px">\n' +
      `  <canvas data-chart="${type}" ${attrs_str || ''} ${data_type == 'url' ? "data-chart-src='" + data + "'" : ''}>\n` +
      '    ' + (data_type !== 'url' ? data.join('\n    ') : '') +
      "\n  </canvas>" +
      "\n</div>";

    return chart_str;
  }, []);

  useEffect(() => {
    if (!editorState) return;

    const cursorAt = editorState.selection.ranges[0].from;
    const line = editorState.doc.lineAt(cursorAt);

    if (!popupMenuState?.isVisible) {
      let content = editorState.doc.toString();
      onChange(content, line.number);
      // setLastContext({content, cursorAt})

      // if (initialDoc?.content !== editorState.doc.toString()) {
      //   onChange();
      // }
    } else {
      const columnIndex = cursorAt - line.from;
      let textBefore = line.text.substring(0, columnIndex);
      const slashAt = textBefore.lastIndexOf('/');

      if (slashAt === -1) {
        return setPopupMenuState({
          isVisible: false
        })
      }

      let searchText = line.text.substring(slashAt + 1, columnIndex);
      let searchEnd = columnIndex;
      const textAfterCursor = line.text.substring(columnIndex);

      if (textAfterCursor?.trim()) {
        searchText += textAfterCursor.split(' ')[0];
        searchEnd = slashAt + 1 + searchText.length;
      }

      setPopupMenuState({
        ...popupMenuState,
        lineInfo: {
          ...line,
          textBefore: '',
          textBehind: line.text.substring(searchEnd),
          searchText: searchText,
          searchStart: slashAt + 1,
          searchEnd,
          textBefore: textBefore.substring(0, slashAt)
        }
      })
    }
  }, [editorState])

  const handleChange = useCallback(
    (state) => {
      setEditorState(state)
    }, [])

  const getSelectionCoords = useCallback(({ view }) => {
    const selection = view.state.selection.main;

    // 使用 CodeMirror 的 coordsAtPos 获取选区结束位置的坐标
    const endCoords = view.coordsAtPos(selection.to);
    const startCoords = view.coordsAtPos(selection.from);

    if (!endCoords || !startCoords) return null;

    // 获取编辑器的边界
    const editorRect = view.scrollDOM.getBoundingClientRect();

    // 计算菜单的初始位置（选区结束位置）
    let x = endCoords.left - editorRect.left;
    let y = endCoords.bottom - editorRect.top;

    // 检查菜单是否会超出编辑器右边界
    const preferredMaxWidth = app === 'slides' ? 310 : 500;
    const menuMaxWidth = Math.min(preferredMaxWidth, Math.max(250, editorRect.width - 20));
    const rightBoundary = editorRect.width;

    if (x + menuMaxWidth > rightBoundary) {
      // 调整为贴着右边界
      x = Math.max(0, rightBoundary - menuMaxWidth);
    }

    // 检查左边界
    if (x < 0) {
      x = 0;
    }

    // 检查是否应该显示在上方还是下方
    const menuHeight = 60; // 估计的菜单高度
    const spaceBelow = editorRect.bottom - endCoords.bottom;
    const spaceAbove = endCoords.top - editorRect.top;
    const popBelow = spaceBelow >= menuHeight || spaceBelow > spaceAbove;

    return {
      x,
      y: popBelow ? y : Math.min(endCoords.top, startCoords.top) - editorRect.top,
      popBelow,
      editorHeight: editorRect.height,
      menuMaxWidth
    };
  }, [app]);

  const toggleBalloonMenu = () => {
    const selection = window.getSelection();

    if (!selection.isCollapsed && editorView) {
      const coords = getSelectionCoords({ view: editorView });

      if (!coords) {
        setBalloonMenuState({ isVisible: false });
        return;
      }

      const lineElement = getCursorLineElement(editorView);

      setBalloonMenuState({
        isVisible: true,
        lineElement,
        maxWidth: coords.menuMaxWidth,
        position: {
          left: coords.x,
          [coords.popBelow ? 'top' : 'bottom']: coords.popBelow
            ? coords.y
            : coords.editorHeight - coords.y,
        }
      });
    } else {
      setBalloonMenuState({ isVisible: false });
    }
  };

  // 使用 CodeMirror 的 coordsAtPos API 获取光标位置
  const getCursorCoords = (view, pos) => {
    // coordsAtPos 返回光标位置的屏幕坐标
    return view.coordsAtPos(pos);
  };

  const calculateMenuPosition = useCallback(({ view, cursorPos, popupMaxHeight, popupMaxWidth }) => {
    // 获取光标的屏幕坐标
    const coords = getCursorCoords(view, cursorPos);

    if (!coords) {
      return null;
    }

    // 获取编辑器容器的边界
    const editorRect = view.scrollDOM.getBoundingClientRect();

    // 计算相对于编辑器容器的位置
    const x = coords.left - editorRect.left;
    const y = coords.bottom - editorRect.top; // 使用 bottom 让菜单显示在光标下方

    // 判断菜单应该显示在光标上方还是下方
    let popBelow = true;
    const spaceBelow = editorRect.bottom - coords.bottom;
    const spaceAbove = coords.top - editorRect.top;

    if (spaceBelow < popupMaxHeight && spaceAbove > spaceBelow) {
      // 下方空间不足且上方空间更大,显示在上方
      popBelow = false;
    }

    // 计算水平位置,确保不超出视口
    let finalX = x;
    const spaceRight = editorRect.right - coords.left;
    if (spaceRight < popupMaxWidth) {
      // 右侧空间不足,尝试向左对齐
      finalX = Math.max(0, x - popupMaxWidth + spaceRight);
    }

    return {
      x: finalX,
      y: popBelow ? y : editorRect.height - coords.top + editorRect.top,
      popBelow
    };
  }, []);

  const getCursorLineElement = (editorView) => {
    const { state } = editorView;
    const pos = state.selection.main.head;

    // 获取光标所在行
    const line = state.doc.lineAt(pos);

    // 通过 domAtPos 获取该行的 DOM 元素
    const domPos = editorView.domAtPos(line.from);
    let lineElement = domPos.node;

    // 向上查找直到找到 .cm-line 元素
    while (lineElement && !lineElement.classList?.contains('cm-line')) {
      lineElement = lineElement.parentElement;
    }

    return lineElement;
  };


  const handleInput = useCallback((view, from, to, text) => {
    setBalloonMenuState({ isVisible: false });

    if (text !== '/') {
      return;
    }

    if (popupMenuState?.isVisible) {
      return;
    }

    const line = view.state.doc.lineAt(to);
    const columnIndex = to - line.from;

    // 检查斜杠后面是否有非空白字符
    if (line.text.charAt(columnIndex)?.trim()) {
      return;
    }

    const lineText = line.text.substring(0, columnIndex) + text + line.text.substring(columnIndex);

    // 计算菜单位置
    const menuPosition = calculateMenuPosition({
      view,
      cursorPos: to, // +1 因为我们刚输入了 '/'
      popupMaxHeight: 310,
      popupMaxWidth: 320
    });

    if (!menuPosition) {
      return;
    }

    const lineElement = getCursorLineElement(view);

    setPopupMenuState({
      ...popupMenuState,
      isVisible: true,
      lineElement,
      position: {
        left: menuPosition.x,
        top: menuPosition.popBelow ? menuPosition.y : undefined,
        bottom: menuPosition.popBelow ? undefined : menuPosition.y,
      },
      lineInfo: {
        ...line,
        lineText
      }
    });
  }, [popupMenuState, calculateMenuPosition]);

  const handleEnterKey = useCallback((view) => {
    // 检查当前行是否包含 '@ai'
    const cursorPos = view.state.selection.main.head;
    const line = view.state.doc.lineAt(cursorPos);
    const lineText = line.text;
    const lowerLineText = lineText.toLowerCase();

    // 查找 '@ai ' (带空格) 或 '@ai' (行尾) 的位置
    let aiIndex = lowerLineText.indexOf('@ai ');
    let hasSpace = true;

    // 如果没找到带空格的 '@ai '，检查是否有 '@ai' 在行尾
    if (aiIndex === -1) {
      const aiIndexNoSpace = lowerLineText.lastIndexOf('@ai');
      if (aiIndexNoSpace !== -1) {
        // 确认 '@ai' 后面是行尾（没有其他字符）
        const afterAi = lowerLineText.substring(aiIndexNoSpace + 3);
        if (afterAi.trim() === '') {
          aiIndex = aiIndexNoSpace;
          hasSpace = false;
        }
      }
    }

    if (aiIndex !== -1) {
      // 找到 '@ai'，解析 selectedText 和 query
      const textBeforeAi = lineText.substring(0, aiIndex).trim();

      // 根据是否有空格来确定偏移量
      const offset = hasSpace ? 4 : 3;
      const textAfterAi = lineText.substring(aiIndex + offset).trim();

      // selectedText: '@ai' 之前的非空文本
      const selectedText = textBeforeAi || view.state.doc.sliceString(0, line.from);
      // query: '@ai' 之后的非空文本
      const query = textAfterAi || '';

      // 获取行元素用于定位
      const lineElement = getCursorLineElement(view);

      // 删除当前行中的 '@ai' (及后面的空格，如果有的话)
      const aiStartPos = line.from + aiIndex;

      view.dispatch({
        changes: { from: aiStartPos, to: line.to, insert: '' }
      });

      // 触发 AI Modal
      dispatch({
        type: AI_ASSISTANT_DIALOG,
        value: {
          caller: app,
          objType: app,
          visible: true,
          trigger: 'atAi',
          anchorEl: lineElement,
          selectedText: selectedText, // '@ai' 之前的文本
          query: query, // '@ai' 之后的文本
          pageContent: view.state.doc.toString()
        }
      });

      return true; // 阻止 Enter 键的默认行为
    }

    return false; // 允许 Enter 键的默认行为
  }, [app, dispatch]);

  const shouldBlockEnter = useCallback(() => {
    // 当 slash menu 或 AI dialog 可见时，屏蔽 Enter 键
    return popupMenuState?.isVisible || aiState.visible;
  }, [popupMenuState?.isVisible, aiState.visible]);

  const [refContainer, editorView] = useCodeMirror({
    initialDoc: initialDoc,
    onChange: handleChange,
    onInput: handleInput,
    onClick: () => {
      setPopupMenuState({ isVisible: false });
    },
    onEnterKey: handleEnterKey,
    shouldBlockEnter: shouldBlockEnter,
    editorReadyCallback: (view) => {
      if (!view || !initialDoc || !location.state?.withAI && !aiDrafter?.visible) {
        return;
      }

      view.dispatch({
        selection: {
          anchor: 0,
          head: view.state.doc.toString().length
        },
      });

      dispatch({
        type: AI_ASSISTANT_DIALOG,
        value: {
          caller: aiDrafter?.visible && 'markdown' || 'slides',
          visible: true,
          trigger: 'entrance',
          objType: aiDrafter?.visible && 'markdown' || 'slides',
          selectedText: aiDrafter?.visible && '' || location.state?.withAI && initialDoc.title,
          anchorEl: Array.from(view.contentDOM.children)[0]
        }
      });

      location.state?.withAI && history.replace({
        ...location,
        state: {
          ...location.state,
          withAI: false
        },
      });

      aiDrafter?.visible && dispatch({
        type: AI_DRAFTER,
        value: {
          visible: false
        }
      })
    }
  })

  useEffect(() => {
    if (editorView) {
      editorView.scrollDOM.onscroll = (event) => {
        onScroll(event, editorView.scrollDOM);
      }
    }
  }, [onScroll])

  useEffect(() => {
    if (editorView) {
      editorView.contentDOM?.addEventListener('blur', onBlur)
    } else {
      // loading editor
    }

    return () => editorView?.contentDOM?.removeEventListener('blur', onBlur)
  }, [editorView, onBlur])

  useEffect(() => {
    if (!editorView?.scrollDOM || scrollScale < 0) return;

    editorView.scrollDOM.scrollTop = scrollScale * (editorView.scrollDOM.scrollHeight - editorView.scrollDOM.getBoundingClientRect().height) / 100
  }, [scrollScale])

  const insert = useCallback((at, text, back) => {
    if (!editorView?.state) {
      return;
    }

    if (at === -1) {
      at = editorView.state.selection.ranges[0]?.from
    }

    editorView.dispatch({
      changes: { from: at, insert: text },
      selection: { anchor: at + text.length - (back ? back : 0) }
    })
  }, [editorView]);

  useImperativeHandle(ref, () => ({
    scrollToLine: (lineNumber) => {
      if (!editorView) return;

      try {
        const line = editorView.state.doc.line(Math.min(lineNumber, editorView.state.doc.lines));

        // 使用 scrollIntoView 滚动到指定行，这不会触发内容变化事件
        editorView.dispatch({
          effects: EditorView.scrollIntoView(line.from, { y: 'start' })
        });

        // 可选：将光标也移动到该行
        editorView.dispatch({
          selection: { anchor: line.from, head: line.from }
        });
      } catch (error) {
        console.error('Error scrolling to line:', error);
      }
    }
  }), [editorView]);

  return <div
    className='editor-wrapper'
    id='editor-wrapper'
    ref={refContainer}
    onMouseUp={toggleBalloonMenu}
    onKeyDown={(event) => {
      if (onKeyDown) {
        onKeyDown(event);
      }

      if (!popupMenuState?.isVisible && !event.shiftKey) {
        if (event.key === 'ArrowUp') {
          cursorLineUp(editorView)
        } else if (event.key === 'ArrowDown') {
          cursorLineDown(editorView)
        }
      } else if (popupMenuState?.isVisible) {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();
          event.stopPropagation();
        }
      }

      // toggleBalloonMenu();
    }}
  >
    <CmdMenu
      app={app}
      state={popupMenuState}
      editorView={editorView}
      insert={insert}
      onClose={() => setPopupMenuState({ isVisible: false })}
      hid={initialDoc?.hid}
    />
    <BalloonMenu
      app={app}
      maxWidth={balloonMenuState.maxWidth || (app === 'slides' ? 310 : 500)}
      state={balloonMenuState}
      editorView={editorView}
      insert={insert}
      onClose={() => {
        setBalloonMenuState({ isVisible: false });
      }}
      hid={initialDoc?.hid}
    />
    {/* <AIModal
      editorView={editorView}
    /> */}
    {/* <CONFIRM_DIALOG /> */}
    <LinkInputModal />
    <PollGenerator />
    <ChartGenerator />
    <TableBuilderModal />
    <SpinnerAndToast />
  </div>
});

export default CmEditor;
